import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, RotateCcw, Loader2, Send, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { openCheckout, goToWhatsapp } from "../lib/payments";
import SectionHeading from "./SectionHeading";

const WHATSAPP_NUMBER = "917709589870";

const OBSTACLES = [
  "Hit a plateau",
  "Lack of time",
  "Inconsistent diet",
  "Work stress & low energy",
  "Confused by conflicting info",
  "Other",
];

const CONDITIONS = [
  "None of these",
  "Thyroid (on medication)",
  "PCOS / PCOD",
  "Diabetes / pre-diabetes",
  "Diabetes (on insulin)",
  "High blood pressure",
  "High cholesterol",
  "Pregnant / breastfeeding",
  "Kidney or liver condition",
  "Heart condition",
];

const HIGH_RISK = [
  "Diabetes (on insulin)",
  "Pregnant / breastfeeding",
  "Kidney or liver condition",
  "Heart condition",
];

const inputCls =
  "w-full rounded-xl bg-ink border border-line px-4 py-3.5 text-white placeholder:text-slate-600 focus:border-accent focus:outline-none transition-colors";

const EMPTY = {
  full_name: "",
  contact: "",
  age_location: "",
  current_weight: "",
  obstacle: "Hit a plateau",
  target_goal: "",
  health_conditions: [],
  medications: "",
  consent: false,
};

export default function IntakeForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consultPaid, setConsultPaid] = useState(false);
  const [paying, setPaying] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleCondition = (c) =>
    setForm((f) => {
      if (c === "None of these") {
        return { ...f, health_conditions: f.health_conditions.includes(c) ? [] : [c] };
      }
      const next = f.health_conditions.filter((x) => x !== "None of these");
      return {
        ...f,
        health_conditions: next.includes(c) ? next.filter((x) => x !== c) : [...next, c],
      };
    });

  const flaggedRisk = form.health_conditions.some((c) => HIGH_RISK.includes(c));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the health declaration to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api("/leads", { method: "POST", body: JSON.stringify(form) });
      setSubmitted(true);
      toast.success("Application received. Nikhil will reach out soon.");
    } catch (err) {
      toast.error(err.message || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const payConsultation = async () => {
    setPaying(true);
    try {
      const res = await openCheckout({
        product: "consultation",
        prefill: { name: form.full_name, contact: form.contact },
        onSuccess: () => {
          setConsultPaid(true);
          toast.success("Consultation booked! Nikhil's team will confirm your slot on WhatsApp.");
        },
      });
      if (res?.fallback) {
        toast.info("Opening WhatsApp — Nikhil will send you a secure payment link right away.");
        goToWhatsapp("consultation");
      }
    } catch (err) {
      toast.info("Opening WhatsApp — Nikhil will send you a secure payment link right away.");
      goToWhatsapp("consultation");
    } finally {
      setPaying(false);
    }
  };

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hi Nikhil! I just applied for coaching through your website. My name is ${form.full_name}. Looking forward to connecting!`
  )}`;

  return (
    <section id="intake-form" className="relative py-24 lg:py-32">
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 h-96 w-96 rounded-full bg-gold/8 blur-[120px]"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="Start Here"
          title="Apply For 1-on-1 Coaching"
          sub="Every application is personally reviewed by Nikhil. If we're a fit, you'll hear back with next steps."
        />
        <div className="mt-14 grid lg:grid-cols-12 gap-6 items-start">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl border border-line bg-panel p-8 lg:p-10"
            data-testid="intake-form-card"
          >
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="py-6 text-center"
                  data-testid="intake-success-screen"
                >
                  <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-accent/15 border border-accent/40">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                  </span>
                  <h3 className="mt-7 font-display text-3xl font-extrabold uppercase text-white">
                    Application Received
                  </h3>
                  <p className="mt-3 text-slate-400 max-w-md mx-auto leading-relaxed">
                    Thanks, {form.full_name.split(" ")[0] || "athlete"} — your details are with Nikhil. Fast-track
                    your application by starting the conversation directly on WhatsApp.
                  </p>
                  <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="intake-whatsapp-cta"
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-accent text-ink font-extrabold uppercase tracking-wide transition-all duration-300 hover:shadow-glow-emerald hover:-translate-y-0.5"
                    >
                      <MessageCircle className="h-5 w-5" />
                      Chat On WhatsApp
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        setForm(EMPTY);
                        setSubmitted(false);
                        setConsultPaid(false);
                      }}
                      data-testid="intake-reset-btn"
                      className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-line text-slate-300 font-semibold transition-colors hover:border-slate-500"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Submit Another
                    </button>
                  </div>
                  <div
                    className="mt-10 rounded-2xl border border-gold/30 bg-gold/5 p-6 sm:p-8 text-left"
                    data-testid="consultation-upsell-card"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                      <div>
                        <p className="font-display text-lg font-extrabold uppercase text-white">
                          Book Your 1-on-1 Consultation
                        </p>
                        <p className="mt-1.5 text-sm text-slate-400">
                          Skip the queue — secure a priority consultation slot with Nikhil.
                        </p>
                      </div>
                      {consultPaid ? (
                        <span
                          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-br from-accent to-accent-dim text-ink font-extrabold uppercase tracking-wide text-sm whitespace-nowrap"
                          data-testid="consultation-paid-badge"
                        >
                          <CheckCircle2 className="h-4 w-4" /> Booked · ₹499
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={payConsultation}
                          disabled={paying}
                          data-testid="intake-pay-consultation-btn"
                          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-ink font-extrabold uppercase tracking-wide text-sm transition-all duration-300 hover:shadow-glow-gold hover:-translate-y-0.5 disabled:opacity-70 whitespace-nowrap"
                        >
                          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                          Book Now · ₹499
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={onSubmit}
                  className="grid sm:grid-cols-2 gap-5"
                  data-testid="intake-form"
                >
                  <div className="sm:col-span-1">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">Full Name</label>
                    <input required value={form.full_name} onChange={set("full_name")} placeholder="Rahul Sharma" className={inputCls} data-testid="intake-name-input" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">WhatsApp Number / Email</label>
                    <input required value={form.contact} onChange={set("contact")} placeholder="+91 98XXXXXXXX or you@email.com" className={inputCls} data-testid="intake-contact-input" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">Age & Location</label>
                    <input required value={form.age_location} onChange={set("age_location")} placeholder="34, Pune" className={inputCls} data-testid="intake-age-location-input" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">Current Weight (kg)</label>
                    <input required type="number" min="30" max="250" value={form.current_weight} onChange={set("current_weight")} placeholder="86" className={inputCls} data-testid="intake-weight-input" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">Primary Obstacle</label>
                    <select value={form.obstacle} onChange={set("obstacle")} className={`${inputCls} appearance-none`} data-testid="intake-obstacle-select">
                      {OBSTACLES.map((o) => (
                        <option key={o} value={o} className="bg-ink">
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-1">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">Target Goal</label>
                    <input required value={form.target_goal} onChange={set("target_goal")} placeholder="Lose 10kg & build lean muscle" className={inputCls} data-testid="intake-goal-input" />
                  </div>
                  <div className="sm:col-span-2 pt-3 border-t border-line">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">
                      Health Screening
                    </label>
                    <p className="text-xs text-slate-500 mb-3.5">
                      Select anything that applies. This decides whether your plan needs medical clearance first —
                      it is never shared with anyone.
                    </p>
                    <div className="flex flex-wrap gap-2.5" data-testid="intake-conditions-group">
                      {CONDITIONS.map((c) => {
                        const active = form.health_conditions.includes(c);
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => toggleCondition(c)}
                            data-testid={`intake-condition-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                            className={`px-4 py-2 rounded-full border text-xs font-semibold transition-all duration-200 ${
                              active
                                ? "border-accent bg-accent/15 text-emerald-300"
                                : "border-line bg-ink text-slate-400 hover:border-slate-500"
                            }`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">
                      Current Medications / Supplements (optional)
                    </label>
                    <input
                      value={form.medications}
                      onChange={set("medications")}
                      placeholder="e.g. Thyronorm 50mcg, Metformin 500mg"
                      className={inputCls}
                      data-testid="intake-medications-input"
                    />
                  </div>
                  {flaggedRisk && (
                    <div
                      className="sm:col-span-2 flex gap-3 rounded-2xl border border-gold/35 bg-gold/5 p-4"
                      data-testid="intake-high-risk-notice"
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-gold" />
                      <p className="text-xs text-slate-300 leading-relaxed">
                        Based on what you selected, your plan will be kept conservative and must be cleared by your
                        treating doctor before you start. Nikhil will discuss this with you personally.
                      </p>
                    </div>
                  )}
                  <label
                    className="sm:col-span-2 flex gap-3 cursor-pointer select-none"
                    data-testid="intake-consent-label"
                  >
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                      data-testid="intake-consent-checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-emerald-500"
                    />
                    <span className="text-xs text-slate-400 leading-relaxed">
                      I confirm the details above are accurate. I understand this is whole-food first nutrition coaching
                      — not medical treatment — and that it does not replace advice from my doctor.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="intake-form-submit-btn"
                    className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-accent to-accent-dim px-8 py-4 font-extrabold uppercase tracking-wide text-ink transition-all duration-300 hover:shadow-glow-emerald disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? "Submitting..." : "Submit Application"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="rounded-3xl border border-line bg-panel p-8" data-testid="intake-process-card">
              <h3 className="font-display text-xl font-bold uppercase text-white">What Happens Next</h3>
              <ol className="mt-6 space-y-5">
                {[
                  { n: "01", t: "Apply", d: "Two minutes. Share your stats, health flags and what you want to change." },
                  { n: "02", t: "Screening & Review", d: "Nikhil personally reviews your application — including any medical flags — and reaches out." },
                  { n: "03", t: "Your Food Protocol", d: "A whole-food first, low-GI plan in grams, daily WhatsApp check-ins and a review every 15 days." },
                ].map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <span className="font-mono text-sm font-bold text-gold">{s.n}</span>
                    <div>
                      <p className="font-semibold text-white">{s.t}</p>
                      <p className="text-sm text-slate-400 mt-0.5">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="intake-direct-whatsapp-card"
              className="block rounded-3xl border border-accent/30 bg-gradient-to-br from-panel to-ink p-8 transition-all duration-300 hover:border-accent/60 hover:shadow-glow-emerald"
            >
              <MessageCircle className="h-7 w-7 text-emerald-400" />
              <p className="mt-4 font-display text-xl font-bold uppercase text-white">Prefer to talk first?</p>
              <p className="mt-2 text-sm text-slate-400">Message Nikhil directly on WhatsApp — usually replies within a day.</p>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}